import { useMemo } from "react";

import type { Account, Margin } from "~/v2/api/rest/account";
import type { CryptoAddress, CryptoAsset } from "~/v2/api/rest/cryptoWallet";
import { getLiveAccountLabels } from "~/v2/utils/accountLabels";
import { parseCryptoAssetSymbol } from "../util";

interface UseDepositProps {
  account: Account | undefined;
  margin: Margin | undefined;
  asset: CryptoAsset | null;
  depositAddress: CryptoAddress | undefined;
  isDepositAddressLoading: boolean;
  isDepositAddressError: boolean;
  refetchDepositAddress: () => void;
}

export interface UseDepositReturn {
  address: CryptoAddress | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  accountLabel: string;
  showNetworkWarning: boolean;
}

export const useDeposit = ({
  account,
  margin,
  asset,
  depositAddress,
  isDepositAddressLoading,
  isDepositAddressError,
  refetchDepositAddress,
}: UseDepositProps): UseDepositReturn => {
  const accountLabel = useMemo(
    () => getLiveAccountLabels({ account, margin }).subTitle,
    [account, margin],
  );

  // ERC-20 deposits must use the Ethereum network; warn so funds aren't sent
  // over an unsupported chain and permanently lost.
  const showNetworkWarning = useMemo(
    () =>
      !!asset &&
      !!depositAddress &&
      parseCryptoAssetSymbol(asset.symbol ?? "").chain.startsWith("ETH"),
    [asset, depositAddress],
  );

  return {
    address: depositAddress,
    isLoading: isDepositAddressLoading,
    isError: isDepositAddressError,
    onRetry: refetchDepositAddress,
    accountLabel,
    showNetworkWarning,
  };
};
